import { HttpClient } from '@adapters/HttpClient';
import { RolePermission, RolesModel } from '../models/Roles';

export interface RolesFilters {
  searchTerm?: string;
  type_ids?: number[];
  product_ids?: number[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  event_ids?: number[];
}

export interface RolesPage {
  roles: RolesModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export class RolesApiAdapter {
  constructor(private httpClient: HttpClient) { }

  async getRolesPaginated(filters: RolesFilters = {}): Promise<RolesPage> {
    const {
      searchTerm = '',
      type_ids,
      product_ids,
      page = 1,
      limit = 15,
    } = filters;

    const params: any = {
      ...(searchTerm && { search: searchTerm }),
      ...(type_ids && { type_ids }),
      ...(product_ids && { product_ids }),
      page,
      per_page: limit,
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/roles', params, {}, showGlobalLoading);

    if (response.meta && Array.isArray(response.data)) {
      return {
        roles: response.data,
        nextPage:
          response.meta.currentPage < response.meta.lastPage
            ? response.meta.currentPage + 1
            : undefined,
        hasMore: response.meta.currentPage < response.meta.lastPage,
        total: response.meta.total,
      };
    } else if (Array.isArray(response)) {
      return {
        roles: response,
        nextPage: undefined,
        hasMore: false,
        total: response.length,
      };
    } else if (Array.isArray(response.data)) {
      return {
        roles: response.data,
        nextPage: undefined,
        hasMore: false,
        total: response.data.length,
      };
    } else {
      return {
        roles: [],
        nextPage: undefined,
        hasMore: false,
        total: 0,
      };
    }
  }

  /**
   * Obtener todos los roles
   * Hace múltiples llamadas si es necesario para obtener todas las páginas
   * @returns Promise con array de todos los roles
   */
  async getAll(): Promise<RolesModel[]> {
    const allRoles: RolesModel[] = [];
    let currentPage = 1;
    let hasMore = true;
    const MAX_PAGES = 100; // Límite de seguridad para evitar bucles infinitos

    // Obtener todas las páginas de roles
    while (hasMore && currentPage <= MAX_PAGES) {
      const response = await this.getRolesPaginated({
        page: currentPage,
        limit: 100, // Límite grande por página para minimizar llamadas
      });

      allRoles.push(...response.roles);

      // Actualizar información de paginación
      hasMore = response.hasMore;

      // Si hay siguiente página, avanzar
      if (response.nextPage) {
        currentPage = response.nextPage;
      } else {
        // Si no hay siguiente página, terminar
        hasMore = false;
      }
    }

    return allRoles;
  }

  async deleteRole(
    roleId: number | string,
    replaceRoleId: number | string
  ): Promise<void> {
    await this.httpClient.delete(`/roles/${roleId}`, {
      data: { replace_role_id: replaceRoleId },
    });
  }

  async createRole(name: string, permissions: string[]): Promise<void> {
    await this.httpClient.post('/roles', { name, permissions });
  }

  async getRoleById(roleId: number | string): Promise<RolesModel> {
    return await this.httpClient.get<RolesModel>(`/roles/${roleId}`);
  }

  async updateRole(
    roleId: number | string,
    name: string,
    permissions: string[]
  ): Promise<RolesModel> {
    const uniquePermissions = Array.from(new Set(permissions));
    const response = await this.httpClient.put<RolesModel>(`/roles/${roleId}`, {
      name,
      permissions: uniquePermissions,
    });

    // Si la API devuelve el rol actualizado, usarlo; si no, obtenerlo
    if (response && response.id) {
      return response;
    }

    // Si no viene en la respuesta, obtenerlo con GET
    return await this.getRoleById(roleId);
  }

  /**
   * Obtener todos los permisos disponibles desde la API
   * La API devuelve un array de strings (slugs)
   * @returns Promise con array de permisos disponibles
   */
  async getPermissions(): Promise<RolePermission[]> {
    const response = await this.httpClient.get<any>('/permission', {}, {}, false);

    // Manejar diferentes formatos de respuesta
    let permissions: string[] = [];

    if (Array.isArray(response)) {
      permissions = response;
    } else if (response && Array.isArray(response.data)) {
      permissions = response.data;
    } else {
      return [];
    }

    // Convertir array de strings (slugs) a objetos RolePermission
    // Generamos un ID temporal basado en el índice para mantener compatibilidad
    return permissions
      .filter((slug) => slug && typeof slug === 'string' && slug.trim().length > 0)
      .map((slug, index) => ({
        id: index + 1, // ID temporal basado en índice
        slug: slug.trim(),
        team_type: '', // La API no devuelve team_type, se deja vacío
      }));
  }
}
